import { Box, Button, Group, Stack, Text } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTootip, XAxis, YAxis } from 'recharts';
import { generateCSV, isSameDay, useChartDatas, useBot } from '@mygpt/app';
import CustomContainer from '@/components/CustomContainer';
import CustomContainerHeader from '@/components/CustomContainer/CustomContainerHeader';
import CustomTooltip from '@/components/Dashboard/Chart/components/CustomTooltip';
import ChartTable from '@/components/Dashboard/ChartTable';
import { SvgFileDownload } from '@/components/Dashboard/icon';
import TitleCard from '@/components/Dashboard/TitleCard';

const TotalConversations = () => {
  const { t } = useTranslation();
  const { botId } = useBot();
  const { datas, times, timesText } = useChartDatas();

  const [avgConversations, setAvgConversations] = useState(0);

  const totalConversations = useMemo(() => {
    let total = 0;
    datas.map((data) => {
      total += data.created_session_count;
    });
    return total;
  }, [datas]);

  const data = useMemo(() => {
    const chartData: { name: string; conversations: number }[] = [];
    times.map((item) => {
      let conversations = 0;
      datas.map((data) => {
        if (isSameDay(item, data.updated_at)) {
          conversations += data.created_session_count;
        }
      });

      const obj = {
        name: item,
        conversations,
      };
      chartData.push(obj);
    });
    return chartData;
  }, [datas, times]);

  const tableData = useMemo(() => {
    const value: { key: string; value: number }[] = [];
    const avgValue: { key: string; value: number }[] = [];
    const avgTotal: { sum: number; users: string[] } = {
      sum: 0,
      users: [],
    };
    times.map((time) => {
      let sum = 0;
      let allusers: string[] = [];
      datas.map((data) => {
        if (isSameDay(time, data.updated_at)) {
          sum += data.created_session_count;
          allusers = allusers.concat(data.new_users || [], data.old_users || []);
        }
      });
      allusers = [...new Set(allusers)];

      value.push({
        key: time,
        value: sum,
      });
      avgValue.push({
        key: time,
        value: allusers.length === 0 ? 0 : Math.ceil(sum / allusers.length),
      });
      avgTotal.sum = avgTotal.sum + sum;
      avgTotal.users = [...new Set(avgTotal.users.concat(allusers))];
    });
    const avgTotalNum = avgTotal.users.length === 0 ? 0 : Math.ceil(avgTotal.sum / avgTotal.users.length);
    setAvgConversations(avgTotalNum || 0);
    return [
      {
        type: t('dashboard.card.total_conversations'),
        value,
      },
      {
        type: t('dashboard.card.avg_conversations'),
        value: avgValue,
        total: avgTotalNum,
      },
    ];
  }, [datas]);

  const DownloadButton = () => {
    const download = () => {
      const fileName = `GBase_${botId}_${t('dashboard.card.total_conversations')}`;
      const header = [t('dashboard.series'), ...times, t('dashboard.total')];
      // 将每个数据对象转换为CSV格式的一行
      const rows = tableData.map((item) => {
        const rowsData = item.value;
        let total = 0;
        const everyCol = rowsData.map((row) => {
          const val = isNaN(row.value) ? 0 : row.value;
          total += val;
          return String(val);
        });
        return [item.type, ...everyCol, String(total)];
      });

      const data = {
        header,
        rows,
      };
      generateCSV(fileName, data);
    };

    return (
      <Button size="sm" variant="subtle" leftSection={<SvgFileDownload />} onClick={download}>
        <Text style={{ color: '#7C68FB' }}>{t('dashboard.export_csv')}</Text>
      </Button>
    );
  };

  return (
    <>
      <CustomContainerHeader
        title={t('dashboard.card.total_conversations_title')}
        label={t('dashboard.card.total_conversations_tip')}
      />
      <CustomContainer>
        <Stack gap={50}>
          <Group>
            <TitleCard title={t('dashboard.card.total_conversations')} value={totalConversations} desc={timesText} />
            <TitleCard title={t('dashboard.card.avg_conversations')} value={avgConversations} desc={timesText} />
          </Group>
          <Box w={'100%'} h={350} className="text-sm text-[#667085] font-light">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={500}
                height={300}
                data={data}
                barGap={48}
                barCategoryGap={48}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid stroke="#E4E7EC" strokeDasharray="0 0" vertical={false} />
                <XAxis axisLine={false} tickLine={false} tickMargin={10} padding={{ left: 45 }} dataKey="name" />
                <YAxis axisLine={false} tickLine={false} tickMargin={10} />
                <RechartsTootip
                  content={<CustomTooltip text={{ conversations: t('dashboard.table.conversations') }} />}
                />
                <Bar dataKey="conversations" fill="#7C68FB" />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <ChartTable
            title={t('dashboard.table.total_conversations_title')}
            titleRightSection={<DownloadButton />}
            times={times}
            data={tableData}
          />
        </Stack>
      </CustomContainer>
    </>
  );
};

export default TotalConversations;
